import React, { useState } from 'react';
import cls from 'classnames';
import { useEggBreakerPairs } from 'contexts/EggBreakerPairContext';
import Spinner from 'components/Spinner';
import './PairBreaker.scss';
import { useUser } from '../../hooks/useUser';
import { PASSIVE_PAIRED_EGG_OMNIGROW } from 'contexts/EggContext';

type Props = {
  gribbleId: number;
  onClose: () => void;
};

const PairBreaker = ({ gribbleId, onClose }: Props) => {
  const { account } = useUser();
  const { pairs, pair, unpair } = useEggBreakerPairs();
  const [breakerToPair, setBreakerToPair] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { breakers } = account;

  const userId = account.dusktech?.userId;

  if (!userId) {
    return <>You must be logged in to pair a breaker</>;
  }

  // Filter out any breakers that are paired to another gribble
  const unpairedBreakers = breakers.filter((breaker) => {
    return !pairs.find((pair) => pair.breakerId === breaker.id && pair.gribbleId !== gribbleId);
  });

  return (
    <div className="modal fade show pair-breaker-modal" tabIndex={-1} id="selected-breaker-modal">
      <div className="modal-backdrop fade show" onClick={onClose}></div>

      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="header">
            <h3 className="title">Pair a breaker</h3>
            <button
              type="button"
              className="btn-close btn-close-white close-modal-btn"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={onClose}
            />
          </div>

          <div className="body">
            {error && <div className="error-message">{error}</div>}

            {!unpairedBreakers.length && (
              <div className="empty-state">You have no breakers to pair.</div>
            )}

            {unpairedBreakers.length > 0 && (
              <>
                <p>
                  Collect <b>{PASSIVE_PAIRED_EGG_OMNIGROW}</b> OmniGrow / Day per Paired Breaker
                </p>

                <div
                  className={cls('breaker-grid', {
                    'is-loading': breakerToPair,
                  })}
                >
                  {unpairedBreakers.map(({ id, imageUrls }) => {
                    const breakerEggPair = pairs.find(
                      ({ gribbleId: pairedGribbleId, breakerId }) => {
                        return pairedGribbleId === gribbleId && breakerId === id;
                      },
                    );

                    const isLoading = breakerToPair === id;

                    return (
                      <div
                        className={cls('breaker', {
                          'is-paired': breakerEggPair,
                          'is-loading': isLoading,
                        })}
                        key={id}
                        onClick={async () => {
                          if (breakerToPair) {
                            return;
                          }

                          setBreakerToPair(id);

                          try {
                            if (breakerEggPair) {
                              await unpair(userId, breakerEggPair.id);
                            } else {
                              // If the clicked breaker is not the currently paired breaker,
                              // we need to unpair all other breakers
                              const pairedBreakers = pairs.filter(
                                ({ gribbleId: pairedGribbleId }) => pairedGribbleId === gribbleId,
                              );
                              if (pairedBreakers.length > 0) {
                                for (const { id: breakerPairId } of pairedBreakers) {
                                  await unpair(userId, breakerPairId);
                                }
                              }

                              await pair(userId, id, gribbleId);
                            }

                            setError(null);
                            onClose();
                          } catch (e) {
                            setError('Something went wrong. Refresh the page and try again.');
                          } finally {
                            setBreakerToPair(null);
                          }
                        }}
                      >
                        <div className="breaker-image-container">
                          <img src={imageUrls.thumbnail} alt={`Breaker #${id}`} />

                          {isLoading && (
                            <Spinner
                              color="white"
                              width={40}
                              height={40}
                              className="breaker-pair-spinner"
                            />
                          )}
                        </div>
                        {breakerEggPair ? `Unpair Breaker` : `Breaker #${id}`}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PairBreaker;
